import React, { useState } from 'react';
import { Shield, Key, Smartphone, Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { usersApi } from '../services/creatorApi';
import { generateSalt, hashPassword, verifyPassword } from '../services/authService';
import { toast } from 'sonner';
export function AccountSettings() {
  const {
    currentUser
  } = useWorkspace();
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  // PIN change state
  const [pinCurrentPassword, setPinCurrentPassword] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [isChangingPin, setIsChangingPin] = useState(false);
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) return;
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setIsChangingPassword(true);
    try {
      // 1. Verify current password
      if (!currentUser.Password_Hash || !currentUser.Password_Salt) {
        throw new Error('Current password not set properly');
      }
      const isValid = await verifyPassword(currentPassword, currentUser.Password_Hash, currentUser.Password_Salt);
      if (!isValid) {
        toast.error('Current password is incorrect');
        setIsChangingPassword(false);
        return;
      }
      // 2. Hash new password
      const newSalt = generateSalt();
      const newHash = await hashPassword(newPassword, newSalt);
      // 3. Update user record
      await usersApi.update(currentUser.ID, {
        Password_Hash: newHash,
        Password_Salt: newSalt
      });
      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Failed to change password:', err);
      toast.error('Failed to update password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };
  const handlePinChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pinCurrentPassword || !newPin || !confirmPin) return;
    if (newPin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }
    setIsChangingPin(true);
    try {
      // 1. Verify current password
      if (!currentUser.Password_Hash || !currentUser.Password_Salt) {
        throw new Error('Current password not set properly');
      }
      const isValid = await verifyPassword(pinCurrentPassword, currentUser.Password_Hash, currentUser.Password_Salt);
      if (!isValid) {
        toast.error('Current password is incorrect');
        setIsChangingPin(false);
        return;
      }
      // 2. Hash new PIN
      const newSalt = generateSalt();
      const newHash = await hashPassword(newPin, newSalt);
      // 3. Update user record
      await usersApi.update(currentUser.ID, {
        Quick_PIN_Hash: newHash,
        Quick_PIN_Salt: newSalt
      });
      toast.success('Quick PIN updated successfully');
      setPinCurrentPassword('');
      setNewPin('');
      setConfirmPin('');
    } catch (err) {
      console.error('Failed to change PIN:', err);
      toast.error('Failed to update Quick PIN. Please try again.');
    } finally {
      setIsChangingPin(false);
    }
  };
  const inputClass = 'block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm';
  return <div className="max-w-4xl mx-auto space-y-8" data-id="element-2282">
      <div data-id="element-2283">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2284">
          Account Settings
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-2285">
          Manage your security preferences and authentication methods.
        </p>
      </div>

      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl overflow-hidden" data-id="element-2286">
        <div className="px-4 py-5 sm:p-6 border-b border-gray-200" data-id="element-2287">
          <div className="flex items-center gap-3 mb-4" data-id="element-2288">
            <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-2289">
              <Key className="h-5 w-5 text-blue-600" data-id="element-2290" />
            </div>
            <div data-id="element-2291">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-2292">
                Change Password
              </h2>
              <p className="text-sm text-gray-500" data-id="element-2293">
                Update your primary account password.
              </p>
            </div>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md mt-6" data-id="element-2294">
            <div data-id="element-2295">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2296">
                Current Password
              </label>
              <div className="relative" data-id="element-2297">
                <input type={showCurrent ? 'text' : 'password'} required className={inputClass} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} data-id="element-2298" />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-2299">
                  {showCurrent ? <EyeOff className="h-4 w-4" data-id="element-2300" /> : <Eye className="h-4 w-4" data-id="element-2301" />}
                </button>
              </div>
            </div>

            <div data-id="element-2302">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2303">
                New Password
              </label>
              <div className="relative" data-id="element-2304">
                <input type={showNew ? 'text' : 'password'} required minLength={8} className={inputClass} value={newPassword} onChange={e => setNewPassword(e.target.value)} data-id="element-2305" />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-2306">
                  {showNew ? <EyeOff className="h-4 w-4" data-id="element-2307" /> : <Eye className="h-4 w-4" data-id="element-2308" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-2309">
                Must be at least 8 characters long.
              </p>
            </div>

            <div data-id="element-2310">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2311">
                Confirm New Password
              </label>
              <input type={showNew ? 'text' : 'password'} required minLength={8} className={inputClass} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} data-id="element-2312" />
            </div>

            <div className="pt-2" data-id="element-2313">
              <button type="submit" disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword} className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50" data-id="element-2314">
                {isChangingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" data-id="element-2315" /> : null}
                Update Password
              </button>
            </div>
          </form>
        </div>

        <div className="px-4 py-5 sm:p-6 bg-gray-50/50" data-id="element-2316">
          <div className="flex items-center gap-3 mb-4" data-id="element-2317">
            <div className="h-10 w-10 bg-purple-50 rounded-lg flex items-center justify-center" data-id="element-2318">
              <Smartphone className="h-5 w-5 text-purple-600" data-id="element-2319" />
            </div>
            <div data-id="element-2320">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-2321">
                Quick PIN / Phrase
              </h2>
              <p className="text-sm text-gray-500" data-id="element-2322">
                Set up a short PIN or phrase to quickly unlock the app after
                brief inactivity.
              </p>
            </div>
          </div>

          <form onSubmit={handlePinChange} className="space-y-4 max-w-md mt-6" data-id="element-2323">
            <div data-id="element-2324">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2325">
                Current Password
              </label>
              <p className="text-xs text-gray-500 mb-2" data-id="element-2326">
                Required to verify your identity before changing your PIN.
              </p>
              <input type="password" required className={inputClass} value={pinCurrentPassword} onChange={e => setPinCurrentPassword(e.target.value)} data-id="element-2327" />
            </div>

            <div data-id="element-2328">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2329">
                New Quick PIN / Phrase
              </label>
              <div className="relative" data-id="element-2330">
                <input type={showPin ? 'text' : 'password'} required className={inputClass} value={newPin} onChange={e => setNewPin(e.target.value)} data-id="element-2331" />
                <button type="button" onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-2332">
                  {showPin ? <EyeOff className="h-4 w-4" data-id="element-2333" /> : <Eye className="h-4 w-4" data-id="element-2334" />}
                </button>
              </div>
            </div>

            <div data-id="element-2335">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2336">
                Confirm Quick PIN
              </label>
              <input type={showPin ? 'text' : 'password'} required className={inputClass} value={confirmPin} onChange={e => setConfirmPin(e.target.value)} data-id="element-2337" />
            </div>

            <div className="pt-2" data-id="element-2338">
              <button type="submit" disabled={isChangingPin || !pinCurrentPassword || !newPin || !confirmPin} className="inline-flex items-center justify-center rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50" data-id="element-2339">
                {isChangingPin ? <Loader2 className="h-4 w-4 animate-spin mr-2" data-id="element-2340" /> : null}
                Set Quick PIN
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
}